<script lang="ts" module>
    import { Chart, type ChartConfiguration, type ChartDataset, type ChartTypeRegistry, type ScriptableChartContext, type Tick, type TimeUnit, type TooltipItem } from "chart.js";
    import { parseIntervalStringToSeconds } from "./DateTimeHelper";
    import type { Totals } from "../model/StatsTotals";

    // let style = getComputedStyle(document.body);
    // let primaryColor = style.getPropertyValue("--bs-primary");
    // let primaryColorRGB = style.getPropertyValue("--bs-primary-rgb");
    // let secondary = style.getPropertyValue("--bs-secondary");
    // let maxColor = style.getPropertyValue("--bs-green");
    // let minColor = style.getPropertyValue("--bs-pink");
    // let avgColor = style.getPropertyValue("--bs-gray");

    export enum CommonAggreations {
        MIN_1 = "1min",
        MIN_5 = "5min",
        MIN_10 = "10min",

        HOUR_1 = "1h",

        DAY_1 = "1d",
        DAY_7 = "1w",
    }

    export enum MyChartType {
        LINE,
        BAR
    };

    export interface DataSetDescription {
        label: string;
        colorIdentifier: string;

    }

    export function convertMsToSString(tooltipItem: TooltipItem<keyof ChartTypeRegistry> | number |string, index: number, ticks: Tick[]) {
        let numberInput: number;
        if(typeof tooltipItem === 'object') {
            return Math.round((tooltipItem.raw as number) / 10) / 100 + " s";
        } else if (typeof tooltipItem === 'string') {
            numberInput = parseFloat(tooltipItem);
        } else {
            numberInput = tooltipItem;
        }
        return Math.round(numberInput / 10) / 100 + " s";
    }

    export let getCssColor = (colorName: string): string => {
        let style = getComputedStyle(document.body);
        return style.getPropertyValue(colorName).trim();
    };

    const toRgbTriplet = (value: string): string | null => {
        if (!value) {
            return null;
        }

        const color = value.trim();
        const rgbMatch = color.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
        if (rgbMatch) {
            return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
        }

        const hexMatch = color.match(/^#([0-9a-fA-F]{6})$/);
        if (hexMatch) {
            const hex = hexMatch[1];
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `${r}, ${g}, ${b}`;
        }

        return null;
    };

    const resolveRgbTriplet = (baseName: string): string => {
        const rgbVar = getCssColor(`--bs-${baseName}-rgb`);
        const rgbTriplet = toRgbTriplet(rgbVar);
        if (rgbTriplet) {
            return rgbTriplet;
        }

        const colorVar = getCssColor(`--bs-${baseName}`);
        const fromHex = toRgbTriplet(colorVar);
        if (fromHex) {
            return fromHex;
        }

        // Safe fallback so chart gradients never throw in production.
        return "13, 110, 253";
    };

    export let createGradient = (ctx: ScriptableChartContext, options) => {
        if (!ctx.chart.chartArea) {
            return;
        }
        let primaryColorRGB = resolveRgbTriplet("primary");
        let gradient = ctx.chart.ctx.createLinearGradient(0, ctx.chart.chartArea.top, 0, ctx.chart.chartArea.bottom);
        gradient.addColorStop(0, "rgba(" + primaryColorRGB + ", 0.5)");
        gradient.addColorStop(1, "rgba(" + primaryColorRGB + ", 0.1)");

        return gradient;
    };


    export let createGradientCallback = (colorA: string, colorB: string) : ((ctx: ScriptableChartContext, options) => CanvasGradient) => {
        return (ctx: ScriptableChartContext, options) => {
            if (!ctx.chart.chartArea) {
                return;
            }
            let primaryColorRGB = resolveRgbTriplet(colorA);
            let secondaryColorRGB = resolveRgbTriplet(colorB);
            let gradient = ctx.chart.ctx.createLinearGradient(0, ctx.chart.chartArea.top, 0, ctx.chart.chartArea.bottom);
            gradient.addColorStop(0, "rgba(" + primaryColorRGB + ", 0.5)");
            gradient.addColorStop(1, "rgba(" + secondaryColorRGB + ", 0.1)");

            return gradient;
        };
    };


    export let getDefaultSingleGraphOptics = (): ChartDataset => {
        let result: ChartDataset = {
            label: "Please Change",
            fill: true,
            backgroundColor: createGradient,
            borderColor: getCssColor("--bs-primary"),
            tension: 0.5,
            pointStyle: false,
            data: [],
        };
        return result;
    };


    /**
     * Converts a timeseries of totals to individual timeseries for each property of totals
     * @param totalsMap
     */
    export function extractFromTimeToTotalsMap(totalsMap: Map<Date, Totals>): {
        "count": Map<Date, number>,
        "uniquePlayers": Map<Date, number>,
        "duration": Map<Date, number>
    } {
        let counts: Map<Date, number> = new Map();
        let uniquePlayers: Map<Date, number> = new Map();
        let durations: Map<Date, number> = new Map();

        totalsMap.forEach((total: Totals, time: Date) => {
            counts.set(time, total.CountImpressions);
            uniquePlayers.set(time, total.CountUniquePlayers);
            durations.set(time, total.DurationS);
        });

        return {
            "count": counts,
            "uniquePlayers": uniquePlayers,
            "duration": durations
        }
    }


    /**
     * Set Suggested min and max for chart
     * @param chart
     * @param from
     * @param to
     */
    export function setMinMaxStep(chart: Chart, from: Date, to: Date, aggreationString: string | false = false) {
        if(aggreationString) {
            let newTo = new Date(to);
            let secondsAggregationWindow = parseIntervalStringToSeconds(aggreationString);
            newTo.setSeconds(newTo.getSeconds() - secondsAggregationWindow);
            chart.config.options.scales["x"].suggestedMax = newTo;
        } else {
            chart.config.options.scales["x"].suggestedMax = to;
        }
        chart.config.options.scales["x"].suggestedMin = from;
        if(aggreationString) {
            let timeUnit: TimeUnit = timeStringToTimeUnit(aggreationString);
            chart.config.options.scales["x"]["time"].unit = timeUnit;
        }
    }

    export function timeStringToTimeUnit(aggregation: string): TimeUnit {
        if(aggregation.indexOf("d") >= 0) {
            return "day";
        } else if(aggregation.indexOf("min") >= 0) {
            return "minute";
        } else if(aggregation.indexOf("h") >= 0) {
            return "hour";
        } else {
            throw "Aggregtation " + aggregation + " not supported";
        }
    }

    export function currencyMarkupCallback(context: TooltipItem<keyof ChartTypeRegistry>) {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(context.parsed.y);
    }

    /**
     * Returns a minimalistic style for charts in overview pages
     */
    export let getDefaultMinimalisticStyle = (): ChartConfiguration => {
        let result: ChartConfiguration = {
            // The type of chart we want to create
            type: "line",

            // The data for our dataset
            data: {
                labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                datasets: [
                    getDefaultSingleGraphOptics()
                ],
            },

            // Configuration options
            options: {
                layout: {
                    padding: {
                        top: 2,
                        bottom: -10,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        ticks: {
                            display: false,
                        },
                        grid: {
                            drawOnChartArea: true,
                        }                    },
                    y: {
                        display: false,
                    },
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return context.parsed.y.toLocaleString();
                            },
                        },
                    },
                },
            },
        };
        return result;
    };
</script>
